/**
 * Video sync debug utility.
 *
 * Features:
 * - Zero performance cost when disabled
 * - Console-controlled enable/disable via window.wandbMediaSyncDebug
 * - Tailored specifically for video sync events
 * - Structured logging with timestamps and context
 *
 * Usage in console:
 * - window.wandbMediaSyncDebug.enable() - Enable debug logging
 * - window.wandbMediaSyncDebug.disable() - Disable debug logging
 * - window.wandbMediaSyncDebug.isEnabled() - Check current state
 */
export const VideoSyncDebugger = {
  enabled: false,
  prefix: '[MEDIA_SYNC]',

  /**
   * Enable debug logging for video sync events.
   */
  enable(): void {
    VideoSyncDebugger.enabled = true;
    console.info(`${VideoSyncDebugger.prefix} Debug logging enabled`);
  },

  /**
   * Disable debug logging for video sync events.
   */
  disable(): void {
    VideoSyncDebugger.enabled = false;
    console.info(`${VideoSyncDebugger.prefix} Debug logging disabled`);
  },

  /**
   * Check if debug logging is currently enabled.
   */
  isEnabled(): boolean {
    return VideoSyncDebugger.enabled;
  },

  /**
   * Log a video sync event with structured data.
   *
   * @param eventType - The type of sync event (play, pause, seeking, etc.)
   * @param videoId - The ID of the video element
   * @param data - Additional event data
   * @param context - Additional context information
   * @param correlationId - Optional correlation ID for tracking event chains
   */
  logEvent(
    eventType: string,
    videoId: string,
    data?: any,
    context?: any,
    correlationId?: string
  ): void {
    if (!VideoSyncDebugger.enabled) {
      return;
    }
    // Only filter out TIME_UPDATE events if they don't have correlation IDs
    if (
      (eventType === 'TIME_UPDATE' || eventType === 'timeupdate') &&
      !correlationId
    ) {
      return;
    }

    const timestamp = new Date().toISOString();
    const duration = performance.now();
    const logData = {
      timestamp,
      duration,
      eventType,
      videoId,
      data,
      context,
      correlationId,
    };

    const groupTitle = correlationId
      ? `${VideoSyncDebugger.prefix} ${eventType} [${videoId}] [${correlationId}]`
      : `${VideoSyncDebugger.prefix} ${eventType} [${videoId}]`;

    // Use groupCollapsed for better organization when correlation IDs are present
    if (correlationId) {
      console.groupCollapsed(groupTitle);
    } else {
      console.group(groupTitle);
    }
    console.log('Event:', logData);
    if (data) {
      console.log('Data:', data);
    }
    if (context) {
      console.log('Context:', context);
    }
    if (correlationId) {
      console.log('Correlation ID:', correlationId);
    }
    console.groupEnd();
  },

  /**
   * Log a sync application event.
   *
   * @param message - The sync message
   * @param videoId - The ID of the video element being synced
   * @param triggerEvent - The event that triggered the sync
   * @param syncData - The sync data being applied
   * @param correlationId - Optional correlation ID for tracking event chains
   */
  logSync(
    message: string,
    videoId: string,
    triggerEvent?: string,
    syncData?: {
      currentTime: number;
      isPlaying: boolean;
      playbackRate: number;
    },
    correlationId?: string,
    videoElement?: HTMLVideoElement
  ): void {
    if (!VideoSyncDebugger.enabled) {
      return;
    }

    const timestamp = new Date().toISOString();
    const duration = performance.now();
    const logData = {
      timestamp,
      duration,
      action: 'SYNC_APPLIED',
      videoId,
      triggerEvent,
      syncData,
      correlationId,
      videoElement,
    };

    const groupTitle = correlationId
      ? `${VideoSyncDebugger.prefix} SYNC [${videoId}] [${correlationId}]`
      : `${VideoSyncDebugger.prefix} SYNC [${videoId}]`;

    // Use groupCollapsed for better organization when correlation IDs are present
    if (correlationId) {
      console.groupCollapsed(groupTitle);
    } else {
      console.group(groupTitle);
    }
    console.log('Sync Event:', logData);
    if (message) {
      console.log('Message:', message);
    }
    if (triggerEvent) {
      console.log('Trigger:', triggerEvent);
    }
    if (syncData) {
      console.log('Sync Data:', syncData);
    }
    if (correlationId) {
      console.log('Correlation ID:', correlationId);
    }
    console.groupEnd();
  },

  /**
   * Log a leader change event.
   *
   * @param newLeaderId - The ID of the new leader video
   * @param previousLeaderId - The ID of the previous leader (if any)
   */
  logLeaderChange(newLeaderId: string, previousLeaderId?: string): void {
    if (!VideoSyncDebugger.enabled) {
      return;
    }

    const timestamp = new Date().toISOString();
    const logData = {
      timestamp,
      action: 'LEADER_CHANGE',
      newLeaderId,
      previousLeaderId,
    };

    console.group(`${VideoSyncDebugger.prefix} LEADER_CHANGE`);
    console.log('Leader Change:', logData);
    console.log('New Leader:', newLeaderId);
    if (previousLeaderId) {
      console.log('Previous Leader:', previousLeaderId);
    }
    console.groupEnd();
  },

  /**
   * Log a sync state update.
   * This is now a no-op to reduce noise in logs.
   *
   * @param state - The current sync state
   * @param source - The source of the state update
   * @param correlationId - Optional correlation ID for tracking event chains
   */
  logStateUpdate(
    state: {
      currentTime: number;
      isPlaying: boolean;
      playbackRate: number;
      lastUpdate: number;
    },
    source: string,
    correlationId?: string
  ): void {
    // Skip STATE_UPDATE logging to reduce noise - focus on meaningful events
    return;
  },
};

// Expose the debugger to the global window object for console control
if (typeof window !== 'undefined') {
  (window as any).wandbMediaSyncDebug = {
    enable: () => VideoSyncDebugger.enable(),
    disable: () => VideoSyncDebugger.disable(),
    isEnabled: () => VideoSyncDebugger.isEnabled(),
  };
}
